import { CoreButtonView, CoreImageView, CoreTextView, type TCoreButtonStyleProps, type TCoreButtonViewProps, type TCoreTextStyleProps } from '@core/widgets';
import { ConditionalRender } from '@libs/core';
import { TRowComponentProps, TViewCommonOption, TWidgetCommonOption } from '@libs/types';
import React, { ChangeEvent, MouseEvent, MouseEventHandler } from 'react';
import { TRowOption, TSelectorRegularOption } from '../../../types/options';
import { TextStyle } from '../../../types/styles';
import { SwitcherView } from '../../Checkbox/SwitcherView';
import { getHref } from '../../Image/imageUtils';
import { VisualView } from '../../Image/VisualView';
import styles from './styles/rowRegular.module.css';

export type TRowRegularViewProps = {
    widget?: TCoreButtonViewProps['widget'];
    uid?: string;
    title?: string;
    subtitle?: string;
    metaText?: string;
    topText?: string;
    value?: boolean;
    iconName?: string;
    flagIcon?: string;
    avatar?: string;
    subtitleIconName?: string;
    iconHierarchy?: TRowOption['iconHierarchy'];
    iconVariant?: TRowOption['iconVariant'];
    metaVariant?: TRowOption['metaVariant'];
    metaHierarchy?: TRowOption['metaHierarchy'];
    variant?: TSelectorRegularOption['variant'];
    margin?: TWidgetCommonOption['margin'];
    isDisabled?: boolean;
    isHide?: boolean;
    handleClick: ((e: MouseEvent | ChangeEvent) => void) | MouseEventHandler<HTMLButtonElement>;
    parentStyleNames?: TViewCommonOption['parentStyleNames'];
} & Pick<TRowComponentProps, 'isDisabled' | 'isHide' | 'isError'>;

const CONTAINER_STYLE_PROPS: TCoreButtonStyleProps = {
    main: 'var(--color-white)',
    onMain: 'var(--color-black)',
    fillContainer: true,
    borderRadius: 0,
    gap: 0,
    padding: { top: 16, right: 24, bottom: 16, left: 24 },
    cursorDisabled: 'default',
    textAlign: 'start',
    disabled: {
        main: 'transparent',
        onMain: 'var(--color-grey_40_lc)',
    },
    hovered: {
        main: 'var(--color-grey_8_lc)',
    },
    pressed: {
        main: 'var(--color-grey_8_lc)',
    },
};

const TITLE_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.main,
    main: 'var(--color-black)',
    disabled: {
        main: 'var(--color-grey_40_lc)',
    },
};

const SUBTITLE_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.secondary,
    main: 'var(--color-grey_72_lc)',
    margin: { top: 4 },
    disabled: {
        main: 'var(--color-grey_40_lc)',
    },
};

const META_STYLE_PROPS_SECONDARY: TCoreTextStyleProps = {
    ...TextStyle.secondary,
    main: 'var(--color-grey_72_lc)',
    disabled: {
        main: 'var(--color-grey_40_lc)',
    },
};

const META_STYLE_PROPS_PRIMARY: TCoreTextStyleProps = {
    ...TextStyle.main,
    main: 'var(--color-black)',
    disabled: {
        main: 'var(--color-grey_40_lc)',
    },
};

export const RowRegularView = ({
    widget,
    uid = '',
    title,
    subtitle,
    metaText,
    topText,
    value = false,
    iconName,
    flagIcon,
    avatar,
    subtitleIconName,
    iconHierarchy,
    iconVariant,
    metaVariant,
    metaHierarchy,
    variant,
    margin,
    isDisabled,
    isError,
    isHide,
    handleClick,
    parentStyleNames = [],
}: TRowRegularViewProps) => {
    const hasLeadingIcon = !variant && (iconName || avatar || flagIcon);
    const effectiveMetaHierarchy = metaHierarchy ?? 'secondary';
    const metaStyleProps = effectiveMetaHierarchy === 'secondary' ? META_STYLE_PROPS_SECONDARY : META_STYLE_PROPS_PRIMARY;

    const renderTrailing = () => {
        if (variant === 'SWITCHER') {
            return (
                <div onClick={(e) => e.stopPropagation()}>
                    <SwitcherView
                        uid={`right_selector_regular_${uid}`}
                        isDisabled={isDisabled}
                        isChecked={value}
                        handleChange={handleClick as (e: React.ChangeEvent<HTMLInputElement>) => void}
                    />
                </div>
            );
        }
        if (variant === 'CHECK' && value) {
            return (
                <CoreImageView
                    src={getHref('ic_check')}
                    isDisabled={isDisabled}
                    parentStyleNames={[...parentStyleNames, 'rowRegularCheck']}
                    styleProps={{ width: 24, height: 24, iconColor: 'var(--color-purple_100)' }}
                />
            );
        }
        if (metaText) {
            void metaVariant;
            return (
                <CoreTextView
                    uid={'meta-' + uid}
                    text={metaText}
                    as="span"
                    isDisabled={isDisabled}
                    parentStyleNames={parentStyleNames}
                    styleName="rowRegularMeta"
                    styleProps={metaStyleProps}
                />
            );
        }
        return null;
    };

    return (
        <CoreButtonView
            widget={widget ?? ({ uid: uid, data: {}, options: {} } as TCoreButtonViewProps['widget'])}
            isDisabled={isDisabled}
            isHide={isHide}
            styleProps={{
                ...CONTAINER_STYLE_PROPS,
                margin: margin,
            }}
            parentStyleNames={parentStyleNames}
            styleName="rowRegular"
            onClick={handleClick as MouseEventHandler<HTMLButtonElement>}
        >
            {hasLeadingIcon && (
                <VisualView
                    uid={`icon-${uid}`}
                    iconName={iconName}
                    avatar={avatar}
                    flagIcon={flagIcon}
                    iconHierarchy={iconHierarchy}
                    iconVariant={iconVariant}
                    size={'small'}
                    isDisabled={isDisabled}
                    rightMargin
                    parentStyleNames={[...parentStyleNames, 'rowRegularIcon']}
                />
            )}
            <div className={styles.rowContent}>
                <ConditionalRender condition={!!topText}>
                    <CoreTextView
                        uid={'topText-' + uid}
                        text={topText}
                        as="span"
                        isDisabled={isDisabled}
                        isError={isError}
                        parentStyleNames={parentStyleNames}
                        styleName="rowRegularTopText"
                        styleProps={{
                            ...TextStyle.secondary,
                            main: 'var(--color-grey_72_lc)',
                            margin: { bottom: 4 },
                            disabled: {
                                main: 'var(--color-grey_40_lc)',
                            },
                            error: {
                                main: 'var(--color-pink_100)',
                            },
                        }}
                    />
                </ConditionalRender>
                <ConditionalRender condition={!!title}>
                    <CoreTextView
                        uid={'title-' + uid}
                        text={title}
                        as="span"
                        isDisabled={isDisabled}
                        parentStyleNames={parentStyleNames}
                        styleName="rowRegularTitle"
                        styleProps={TITLE_STYLE_PROPS}
                    />
                </ConditionalRender>
                <ConditionalRender condition={!!subtitle}>
                    <div className={styles.subtitleRow}>
                        <ConditionalRender condition={!!subtitleIconName}>
                            <CoreImageView
                                src={getHref(subtitleIconName)}
                                isDisabled={isDisabled}
                                parentStyleNames={parentStyleNames}
                                styleName={'rowRegularSubtitleIcon'}
                                styleProps={{
                                    width: 16,
                                    height: 16,
                                    iconColor: 'var(--color-grey_72_lc)',
                                    margin: { right: 8, top: 4 },
                                }}
                            />
                        </ConditionalRender>
                        <CoreTextView
                            uid={'subtitle-' + uid}
                            text={subtitle}
                            as="span"
                            isDisabled={isDisabled}
                            parentStyleNames={parentStyleNames}
                            styleName="rowRegularSubtitle"
                            styleProps={SUBTITLE_STYLE_PROPS}
                        />
                    </div>
                </ConditionalRender>
            </div>
            {renderTrailing()}
        </CoreButtonView>
    );
};
